import { plateFont, numFont } from '../../utils/fonts';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import axios from 'axios';

const DEBOUNCE_MS = 300;

/**
 * Work-order typeahead over every work order of the organization (work number, plate,
 * customer, vehicle), modelled on the picker in the inbound lähete dialog. With an empty
 * query the endpoint returns the newest open work orders, so the list is useful on open.
 *
 * `value` / `onChange` carry the option object `{id, work_number, registration_number,
 * vehicle_name, customer_name, state}` (or null).
 */
export function workOrderLabel(wo) {
  if (!wo) return '';
  return [`#${wo.work_number}`, wo.registration_number, wo.vehicle_name, wo.customer_name].filter(Boolean).join(' · ');
}

function WorkOrderSearchPicker({ value, onChange, label, placeholder, size = 'small', autoFocus = false, disabled = false, sx, testId = 'wo-search-picker' }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef(null);
  const requestRef = useRef(0);

  const search = (term) => {
    window.clearTimeout(debounceRef.current);
    const requestId = ++requestRef.current;
    setLoading(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const response = await axios.get('/api/inbound-delivery-notes/work-order-search/', {
          params: term ? { search: term } : {},
        });
        if (requestId !== requestRef.current) return;
        setOptions(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        if (requestId === requestRef.current) setOptions([]);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    }, term ? DEBOUNCE_MS : 0);
  };

  useEffect(() => {
    search('');
    return () => window.clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Autocomplete
      size={size}
      sx={sx}
      options={options}
      value={value || null}
      inputValue={inputValue}
      loading={loading}
      disabled={disabled}
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={workOrderLabel}
      renderOption={(props, wo) => (
        <li {...props} key={wo.id} data-testid={`${testId}-option-${wo.id}`}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Box component="span" sx={{ ...numFont, fontWeight: 600 }}>#{wo.work_number}</Box>
              {wo.registration_number && (
                <Box component="span" sx={{ ...plateFont, fontWeight: 600 }}>{wo.registration_number}</Box>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {[wo.vehicle_name, wo.customer_name].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </li>
      )}
      onInputChange={(_, val, reason) => {
        setInputValue(val);
        if (reason === 'input') search(val.trim());
      }}
      onChange={(_, val) => onChange(val)}
      noOptionsText={loading ? t('workOrderPicker.searching') : t('workOrderPicker.noResults')}
      data-testid={testId}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder || t('workOrderPicker.placeholder')}
          autoFocus={autoFocus}
          inputProps={{ ...params.inputProps, 'data-testid': `${testId}-input` }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default WorkOrderSearchPicker;
